import * as React from "react";
import { Eye } from "lucide-react";
import { useAuthStore, type PreviewPersona } from "@/store/authStore";
import { useStopViewAs, useSwitchTenant } from "@/lib/impersonation";
import { personaLabel } from "@/components/layout/nav";

/**
 * Thin amber banner pinned above the Header surfacing three independent
 * elevated-scope states:
 *
 *   1. **Tenant impersonation** — SuperAdmin acting as a client.
 *      Drives data-scope (X-Acting-As-Client) + branding swap.
 *   2. **View as a user** (MV2-1250) — SuperAdmin seeing the app as a
 *      real user of the acting-as client (X-View-As-User): their role,
 *      policies and sites, read-only (backend.md §108).
 *   3. **Persona preview** — SuperAdmin previewing another persona on
 *      the /getting-started page (`?preview_persona=`, tour QA).
 *      Render-layer only; does not change permissions or API responses.
 *
 * Any flag alone or several together render this banner. Each has its
 * own clear button so the user can step out of one without losing the
 * other context.
 *
 * Layout:
 *   ≥ 640px — flag(s) on a row + clear button(s) right-aligned.
 *   < 640px — flags stack; clear buttons collapse into compact pills.
 *
 * Renders nothing for non-SuperAdmin or when every flag is null.
 */
const PERSONA_LABELS: Record<PreviewPersona, string> = {
  superadmin: "Super Admin",
  clientadmin: "Client Admin",
  om: "O&M engineer",
  siteowner: "Site Owner",
};

export function ImpersonationBanner(): React.ReactElement | null {
  const actingAs = useAuthStore((s) => s.actingAs);
  // MV2-844 (R1/R2/R3): leaving impersonation invalidates like entering does.
  const switchTo = useSwitchTenant();
  const previewPersona = useAuthStore((s) => s.previewPersona);
  const setPreviewPersona = useAuthStore((s) => s.setPreviewPersona);
  const viewingAs = useAuthStore((s) => s.viewingAs);
  // MV2-1254 — one click back to yourself, where you were before.
  const stopViewAs = useStopViewAs();

  if (!actingAs && !previewPersona && !viewingAs) return null;

  const stopActing = () => switchTo(null);
  return (
    <div
      role="status"
      className="flex min-h-7 flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-[color:color-mix(in_srgb,var(--warning)_34%,transparent)] bg-[color:color-mix(in_srgb,var(--warning)_14%,transparent)] px-[18px] py-1 text-[12px] text-gray-900"
      data-testid="impersonation-banner"
    >
      {actingAs && (
        <>
          <Eye
            size={14}
            className="flex-none text-[color:var(--warning)]"
            aria-hidden
          />
          <span className="min-w-0 truncate">
            Acting as <b className="font-semibold">{actingAs.clientName}</b>
            <span className="hidden sm:inline">
              {" "}
              — you see what their ClientAdmin sees. Writes are audited against
              your account.
            </span>
          </span>
        </>
      )}
      {viewingAs && (
        <>
          <Eye
            size={14}
            className="flex-none text-[color:var(--warning)]"
            aria-hidden
          />
          <span className="min-w-0 truncate">
            Viewing as <b className="font-semibold">{viewingAs.name}</b>
            {viewingAs.persona && ` · ${personaLabel(viewingAs.persona)}`}
            <span className="hidden sm:inline">
              {" "}
              — what they see, read-only.
            </span>
          </span>
        </>
      )}
      {previewPersona && (
        <>
          <Eye
            size={14}
            className="flex-none text-[color:var(--warning)]"
            aria-hidden
          />
          <span className="min-w-0 truncate">
            Viewing as{" "}
            <b className="font-semibold">{PERSONA_LABELS[previewPersona]}</b>
          </span>
        </>
      )}
      <span className="flex-1" />
      {actingAs && (
        <button
          type="button"
          onClick={stopActing}
          className="text-[12px] font-medium text-gray-900 underline-offset-2 hover:underline"
        >
          Stop acting as
        </button>
      )}
      {viewingAs && (
        <button
          type="button"
          onClick={stopViewAs}
          className="text-[12px] font-medium text-gray-900 underline-offset-2 hover:underline"
        >
          Stop viewing as
        </button>
      )}
      {previewPersona && (
        <button
          type="button"
          onClick={() => setPreviewPersona(null)}
          className="text-[12px] font-medium text-gray-900 underline-offset-2 hover:underline"
        >
          Stop preview
        </button>
      )}
    </div>
  );
}
